/**
 * API hooks for the Sales Copilot mobile app.
 *
 * Uses a copilot-specific axios instance that redirects to /copilot/login
 * on 401 instead of the main app /login. Interceptor logic is shared with
 * the main-app instance via createAuthedAxios() in services/api.ts.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { createAuthedAxios } from "../../services/api";
import type { AxiosInstance } from "axios";
import type {
  TripReport,
  TripReportListResponse,
  AccountSearchResult,
  AccountSearchResponse,
  AccountListItem,
  AccountListResponse,
  AccountExportRequest,
  AccountExportItem,
  AccountExportResponse,
  WeeklyDigest,
  ActionItemsResponse,
  SalespersonActionItem,
  OpenOrdersResponse,
  OpenOrderSummary,
  OpenOrderStats,
} from "../types";

export const copilotApi: AxiosInstance = createAuthedAxios({
  loginRedirectUrl: "/copilot/login",
});

// ---------------------------------------------------------------------------
// useRecentTripReports — fetch current user's recent trip reports
// ---------------------------------------------------------------------------

interface UseRecentTripReportsReturn {
  reports: TripReport[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRecentTripReports(limit = 10): UseRecentTripReportsReturn {
  const [reports, setReports] = useState<TripReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await copilotApi.get<TripReportListResponse>(
        "/copilot/trip-reports/recent",
        { params: { limit } }
      );
      setReports(res.data.trip_reports);
    } catch {
      // Silently handle auth errors — show empty state instead of redirecting
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { reports, loading, error, refetch: fetch };
}

// ---------------------------------------------------------------------------
// useAccounts — fetch full account list for directory / selector
// ---------------------------------------------------------------------------

interface UseAccountsReturn {
  accounts: AccountListItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAccounts(): UseAccountsReturn {
  const [accounts, setAccounts] = useState<AccountListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await copilotApi.get<AccountListResponse>(
        "/copilot/accounts"
      );
      setAccounts(res.data.accounts);
    } catch {
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { accounts, loading, error, refetch: fetch };
}

// ---------------------------------------------------------------------------
// useAccountSearch — debounced account search
// ---------------------------------------------------------------------------

interface UseAccountSearchReturn {
  results: AccountSearchResult[];
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
}

export function useAccountSearch(): UseAccountSearchReturn {
  const [results, setResults] = useState<AccountSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const search = useCallback((query: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await copilotApi.get<AccountSearchResponse>(
          "/copilot/accounts/search",
          { params: { q: query.trim(), limit: 10 } }
        );
        setResults(res.data.accounts);
        setError(null);
      } catch {
        setResults([]);
        setError(null);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return { results, loading, error, search };
}

// ---------------------------------------------------------------------------
// useAccountContacts — fetch known contacts for an account
// ---------------------------------------------------------------------------

export interface AccountContact {
  name: string;
  frequency: number;
}

interface AccountContactsResponse {
  contacts: AccountContact[];
}

interface UseAccountContactsReturn {
  contacts: AccountContact[];
  loading: boolean;
}

export function useAccountContacts(
  accountUuid: string | null
): UseAccountContactsReturn {
  const [contacts, setContacts] = useState<AccountContact[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!accountUuid) {
      setContacts([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    copilotApi
      .get<AccountContactsResponse>(
        `/copilot/accounts/${accountUuid}/contacts`
      )
      .then((res) => {
        if (!cancelled) {
          setContacts(res.data.contacts);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContacts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountUuid]);

  return { contacts, loading };
}

// ---------------------------------------------------------------------------
// Home tab hooks
// ---------------------------------------------------------------------------

/** Fetch the latest company-wide weekly digest. */
export function useWeeklyDigest() {
  const [digest, setDigest] = useState<WeeklyDigest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    copilotApi
      .get<WeeklyDigest | null>("/copilot/digest")
      .then((res) => {
        if (!cancelled) setDigest(res.data);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load digest");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return { digest, loading, error };
}

/** Fetch action items for the current salesperson. */
export function useMyActionItems(limit = 20) {
  const [items, setItems] = useState<SalespersonActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    copilotApi
      .get<ActionItemsResponse>("/copilot/action-items", {
        params: { limit, days: 90 },
      })
      .then((res) => {
        if (!cancelled) setItems(res.data.action_items);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load action items");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [limit]);

  return { items, loading, error };
}

/** Fetch open orders for the current salesperson. */
export function useMyOpenOrders(limit = 20) {
  const [orders, setOrders] = useState<OpenOrderSummary[]>([]);
  const [stats, setStats] = useState<OpenOrderStats>({
    draft_count: 0,
    submitted_count: 0,
    confirmed_count: 0,
    accepted_count: 0,
    shipped_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    copilotApi
      .get<OpenOrdersResponse>("/copilot/open-orders", {
        params: { limit },
      })
      .then((res) => {
        if (!cancelled) {
          setOrders(res.data.orders);
          setStats(res.data.stats);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load orders");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [limit]);

  return { orders, stats, loading, error };
}

// ---------------------------------------------------------------------------
// useAccountExport — bulk export contact data for selected accounts
// ---------------------------------------------------------------------------

interface UseAccountExportReturn {
  exportAccounts: (req: AccountExportRequest) => Promise<AccountExportItem[]>;
  loading: boolean;
  error: string | null;
}

export function useAccountExport(): UseAccountExportReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportAccounts = useCallback(
    async (req: AccountExportRequest): Promise<AccountExportItem[]> => {
      setLoading(true);
      setError(null);
      try {
        const res = await copilotApi.post<AccountExportResponse>(
          "/copilot/accounts/export",
          req
        );
        return res.data.items;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Export failed";
        setError(msg);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { exportAccounts, loading, error };
}
